import axios from 'axios'
import { GeoJSON, FeatureCollection } from 'geojson'
import { DateTime } from 'luxon'

import { CoreAPI } from '@/api/core/CoreAPI'
import { City } from '@/model/City'
import { TimeStep } from '@/model/TimeStep'
import { BaseService } from '../BaseService'
import { IMapService } from './IMapService'
import { GeoJsonCache } from './cache/GeoJsonCache'
import { Catchment } from '@/model/Catchment'
import { MapBoxGL } from '@/views/secure/overview/map/MapBoxGL'
import { Point } from '@turf/turf'
import { ServiceLocator } from '../ServiceLocator'
import { ISettingService } from '../setting/ISettingService'
import { AdministrativeArea } from '@/model/AdministrativeArea'
import { AdministrativeAreaFactory } from '../snapshot/AdministrativeAreaFactory'
import { TimeStepMax } from '@/model/TimeStepMax'
import { CatchmentFactory } from '../snapshot/CatchmentFactory'

export class MapService extends BaseService implements IMapService {
  private readonly api: CoreAPI = new CoreAPI()
  private readonly arbitraryCache = new GeoJsonCache()

  private readonly emptyGeoJson: FeatureCollection = {
    type: 'FeatureCollection',
    features: [],
  }

  private settingService!: ISettingService
  private cellSize: number | null = null

  constructor(serviceLocator: ServiceLocator) {
    super(serviceLocator)
    this.settingService = serviceLocator.get<ISettingService>('setting')
  }

  private async getCellSize(): Promise<number> {
    if (this.cellSize == null) {
      const settings = await this.settingService.getSettings()
      this.cellSize = settings.getCellSize()
    }

    return this.cellSize
  }

  async fetchRainfall(city: City, timeStep: TimeStep): Promise<GeoJSON> {
    let geoJson = null
    if (timeStep.isForecast()) {
      geoJson = await this.api.getRainfallForecastMap(
        city.getName(),
        timeStep.snapshot.timestamp.toUTC(),
        timeStep.timestamp.toUTC(),
      )
    } else if (timeStep.isMax()) {
      geoJson = await this.api.getRainfallMaxMap(city.getName(), timeStep.timestamp.toUTC())
    } else {
      geoJson = await this.api.getRainfallMap(city.getName(), timeStep.timestamp.toUTC())
    }

    return geoJson ? await this.convertPointToPolygon(geoJson as FeatureCollection) : this.emptyGeoJson
  }

  async fetchCumulativeRainfall(
    city: City,
    timeStep: TimeStep,
    cumulativeRainfall: number,
    aggregationMinutes: number,
  ): Promise<GeoJSON | null> {
    let geoJson = null
    const roundedTimestamp = this.roundToInterval(timeStep.timestamp.toUTC(), aggregationMinutes)

    if (timeStep.isForecast()) {
      const roundedSnapshotTimestamp = this.roundToInterval(timeStep.snapshot.timestamp.toUTC(), aggregationMinutes)
      geoJson = await this.api.getCumulativeRainfallForecastMap(
        city.getName(),
        cumulativeRainfall,
        roundedSnapshotTimestamp,
        roundedTimestamp,
      )
    } else {
      geoJson = await this.api.getCumulativeRainfallMap(city.getName(), cumulativeRainfall, roundedTimestamp)
    }

    return geoJson ? await this.convertPointToPolygon(geoJson as FeatureCollection) : this.emptyGeoJson
  }

  private roundToInterval(timestamp: DateTime, intervalMinutes: number): DateTime {
    const totalMinutes = timestamp.hour * 60 + timestamp.minute
    const roundedMinutes = intervalMinutes * Math.floor(totalMinutes / intervalMinutes)
    return timestamp.set({
      hour: Math.floor(roundedMinutes / 60),
      minute: roundedMinutes % 60,
      second: 0,
      millisecond: 0,
    })
  }

  async fetchReturnPeriod(city: City, timeStep: TimeStep): Promise<GeoJSON> {
    let geoJson = null
    if (timeStep.isForecast()) {
      geoJson = await this.api.getReturnPeriodForecastMap(
        city.getName(),
        timeStep.snapshot.timestamp.toUTC(),
        timeStep.timestamp.toUTC(),
      )
    } else if (timeStep.isMax()) {
      geoJson = await this.api.getReturnPeriodMaxMap(city.getName(), timeStep.timestamp.toUTC())
    } else {
      geoJson = await this.api.getReturnPeriodMap(city.getName(), timeStep.timestamp.toUTC())
    }

    return geoJson ? await this.convertPointToPolygon(geoJson as FeatureCollection) : this.emptyGeoJson
  }

  async fetchFloodDepth(city: City, timeStep: TimeStep, scale: number): Promise<GeoJSON> {
    let catchments: Catchment[] = []
    if (timeStep instanceof TimeStepMax) {
      catchments = timeStep.catchments
    } else {
      let dtos = null
      if (timeStep.isForecast()) {
        dtos = await this.api.getCatchmentsForecast(
          city.getName(),
          timeStep.snapshot.timestamp.toUTC(),
          timeStep.timestamp.toUTC(),
        )
      } else {
        dtos = await this.api.getCatchments(city.getName(), timeStep.timestamp.toUTC())
      }
      catchments = dtos?.map((c) => CatchmentFactory.fromDTO(c)) || []
    }

    const geoJsonUrls = catchments.map((catchment: Catchment) => catchment.getForScale(scale))
    const geoJsons = await Promise.all(
      geoJsonUrls.map(async (url: string | null): Promise<FeatureCollection> => {
        if (!url) {
          return this.emptyGeoJson
        }

        let geoJson = this.arbitraryCache.retrieve(url)
        if (geoJson) {
          return geoJson
        }

        try {
          const response = await axios.get(url)
          geoJson = response.data
        } catch (exception) {
          console.error(exception)
        }

        if (!geoJson) {
          return this.emptyGeoJson
        }

        geoJson = await this.convertPointToPolygon(geoJson, scale)
        this.arbitraryCache.store(url, geoJson)

        return geoJson
      }),
    )

    const result = { ...this.emptyGeoJson }
    for (const geoJson of geoJsons) {
      result.features = result.features.concat(geoJson.features)
    }

    return result
  }

  async convertPointToPolygon(geoJson: FeatureCollection, scale: number | null = null): Promise<FeatureCollection> {
    if (scale == null) {
      scale = await this.getCellSize()
    }
    for (const feature of geoJson.features) {
      feature.geometry = MapBoxGL.convertPointToPolygon(feature.geometry as Point, scale)
    }
    return geoJson
  }

  async getAdministrativeArea(city: City, timeStep: TimeStep): Promise<AdministrativeArea[]> {
    let dtos = null
    if (timeStep.isForecast()) {
      dtos = await this.api.getAdministrativeAreasForecast(
        city.getName(),
        timeStep.snapshot.timestamp.toUTC(),
        timeStep.timestamp.toUTC(),
      )
    } else {
      dtos = await this.api.getAdministrativeAreas(city.getName(), timeStep.timestamp.toUTC())
    }

    if (!dtos) {
      return []
    }
    return dtos.map((a) => AdministrativeAreaFactory.fromDTO(a))
  }
}
